/**
 * Self-serve signup (19/10/2026): what the signup page asks, stored in the auth
 * metadata `signup` and recorded by the `claim_my_company` RPC. The domain and
 * personal-mailbox rules mirror `normalize_website_domain` and
 * `is_personal_email_domain` (migration 20261019100000_company_signup.sql).
 */

export const SALES_TEAM_SIZES = [
  { key: 'solo', label: 'Juste moi' },
  { key: '2-5', label: '2 à 5' },
  { key: '6-15', label: '6 à 15' },
  { key: '16-50', label: '16 à 50' },
  { key: '50+', label: 'Plus de 50' },
] as const;

export const SIGNUP_ROLES = [
  { key: 'founder', label: 'Dirigeant' },
  { key: 'head_of_sales', label: 'Head of Sales' },
  { key: 'sales', label: 'Commercial' },
  { key: 'other', label: 'Autre' },
] as const;

export type SalesTeamSize = (typeof SALES_TEAM_SIZES)[number]['key'];
export type SignupRole = (typeof SIGNUP_ROLES)[number]['key'];

export interface CompanySignup {
  company: string;
  website: string;
  size: SalesTeamSize;
  role: SignupRole;
}

export const PERSONAL_EMAIL_DOMAINS: readonly string[] = [
  'gmail.com', 'googlemail.com', 'outlook.com', 'outlook.fr', 'hotmail.com', 'hotmail.fr',
  'live.com', 'live.fr', 'msn.com', 'yahoo.com', 'yahoo.fr', 'icloud.com', 'me.com',
  'orange.fr', 'wanadoo.fr', 'free.fr', 'sfr.fr', 'laposte.net', 'gmx.fr', 'gmx.com',
  'proton.me', 'protonmail.com', 'aol.com',
];

export const SIGNUP_PASSWORD_MIN_LENGTH = 10;

const DOMAIN_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

/** Website or URL -> bare lower-case host without www. (null when not a domain). */
export const normalizeWebsiteDomain = (website: string): string | null => {
  const host = website.trim().toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
    .replace(/[/?#:].*$/, '')
    .replace(/^www\.|\.$/g, '');
  return DOMAIN_PATTERN.test(host) && host.length <= 253 ? host : null;
};

export const isProfessionalEmail = (email: string): boolean => {
  const domain = email.trim().toLowerCase().split('@')[1];
  return Boolean(domain) && !PERSONAL_EMAIL_DOMAINS.includes(domain as string);
};
